import ContactUs from "./components/ContactUs";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WhyChooseUs from "./components/WhyChooseUs";

function highlightText(
  text: string,
  phrases: string[] = [],
  highlightClass = "text-[#014F9F] font-semibold"
) {
  if (!phrases || phrases.length === 0) return text;

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const sortedPatterns = [...phrases]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);

  if (sortedPatterns.length === 0) return text;

  const regex = new RegExp(`(${sortedPatterns.join("|")})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, idx) => {
    const isMatch = phrases.some((p) => p.toLowerCase() === part.toLowerCase());
    return isMatch ? (
      <span key={idx} className={highlightClass}>
        {part}
      </span>
    ) : (
      part
    );
  });
}

export function ProductsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace(/^#/, "");
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <main>
      <Main />
      <Content />
      <WhyChooseUs />
      <ContactUs />
    </main>
  );
}

function Main() {
  const { t } = useTranslation();
  
  return (
    <div
      className="py-16 relative"
      style={{
        backgroundImage: "url(/assets/img/products/main.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container px-8 md:px-[100px] py-[130px] relative z-10">
        <h1 className="text-left md:leading-[64px] text-[40px] md:text-5xl font-light text-white mb-8">
          {t('ProductsPage.main.title')}
        </h1>
      </div>
    </div>
  );
}

function Content() {
  const { t } = useTranslation();
  
  const products = [
    {
      title: t('ProductsPage.content.products.galvanized.title'),
      description: t('ProductsPage.content.products.galvanized.description'),
      features: [
        t('ProductsPage.content.products.galvanized.features.feature1'),
        t('ProductsPage.content.products.galvanized.features.feature2'),
        t('ProductsPage.content.products.galvanized.features.feature3'),
        t('ProductsPage.content.products.galvanized.features.feature4'),
        t('ProductsPage.content.products.galvanized.features.feature5'),
        t('ProductsPage.content.products.galvanized.features.feature6'),
      ],
      img: "/assets/img/products/Galvanizado.jpg",
    },
    {
      title: t('ProductsPage.content.products.pickled.title'),
      description: t('ProductsPage.content.products.pickled.description'),
      features: [
        t('ProductsPage.content.products.pickled.features.feature1'),
        t('ProductsPage.content.products.pickled.features.feature2'),
        t('ProductsPage.content.products.pickled.features.feature3'),
        t('ProductsPage.content.products.pickled.features.feature4'),
      ],
      img: "/assets/img/products/Decapado.jpg",
    },
    {
      title: t('ProductsPage.content.products.coldRolled.title'),
      description: t('ProductsPage.content.products.coldRolled.description'),
      features: [
        t('ProductsPage.content.products.coldRolled.features.feature1'),
        t('ProductsPage.content.products.coldRolled.features.feature2'),
        t('ProductsPage.content.products.coldRolled.features.feature3'),
      ],
      img: "/assets/img/products/Rolado-en-Frio.jpg",
    },
    {
      title: t('ProductsPage.content.products.aluminized.title'),
      description: t('ProductsPage.content.products.aluminized.description'),
      img: "/assets/img/products/Aluminizado.jpg",
      features: [
        t('ProductsPage.content.products.aluminized.features.feature1'),
        t('ProductsPage.content.products.aluminized.features.feature2'),
      ],
    },
    {
      title: t('ProductsPage.content.products.enamel.title'),
      description: t('ProductsPage.content.products.enamel.description'),
      img: "/assets/img/products/Spe-Vit1.jpeg",
      features: [
        t('ProductsPage.content.products.enamel.features.feature1'),
        t('ProductsPage.content.products.enamel.features.feature2'),
      ],
    },
    {
      title: t('ProductsPage.content.products.prePainted.title'),
      description: t('ProductsPage.content.products.prePainted.description'),
      img: "/assets/img/products/Pre-Pintado.jpg",
      features: [
        t('ProductsPage.content.products.prePainted.features.feature1'),
      ],
    },
    {
      title: t('ProductsPage.content.products.tinPlate.title'),
      description: t('ProductsPage.content.products.tinPlate.description'),
      img: "/assets/img/products/Estañado.jpg",
      highlightPhrases: [
        t('ProductsPage.content.products.tinPlate.highlightPart1'),
        t('ProductsPage.content.products.tinPlate.highlightPart2'),
      ],
    },
  ];
  const [selectedProductIndex, setSelectedProductIndex] = useState<number>(0);
  const [showResources, setShowResources] = useState(false);

  const selectedProduct = products[selectedProductIndex];

  const handleSelectProduct = (product: any) => {
    const index = products.findIndex(p => p.title === product.title);
    if (index !== -1) {
      setSelectedProductIndex(index);
    }
  };

  const handleDownloadPDF = (fileName: string) => {
    const link = document.createElement("a");
    link.href = `/assets/docs/resources/${fileName}`;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="md:pb-32 pb-16 w-full relative md:bg-[#F2F6FB]">
      <img
        className="hidden md:block absolute top-0 left-0 z-0"
        src="/assets/img/diagonal-left.png"
        alt="Diagonal"
        loading="lazy"
        decoding="async"
      />
      <div className="mx-auto px-8 md:px-28 md:pt-16 relative z-10">
        <div
          className={`pt-12 transition-all duration-1000 delay-600 space-y-4 md:space-y-12`}
        >
          <div className="xl:flex space-y-4 md:space-y-12 md:space-y-0 justify-between">
            <div className="w-full text-center md:text-left">
              <h2 className="md:text-6xl text-4xl font-light">{t('ProductsPage.content.title')}</h2>
            </div>
            <div className="w-full relative">
              <div className="md:text-base text-sm font-medium md:mb-14 md:text-left text-center">
                <p className={`text-[#81848A]`}>
                  {t('ProductsPage.content.subtitle')}{" "}
                  <span className="text-[#014F9F]">
                    {t('ProductsPage.content.subtitleBold')}{" "}
                  </span>
                  {t('ProductsPage.content.subtitle2')}
                </p>
              </div>
            </div>
          </div>
          {/** Desktop Products List */}
          <div className="hidden md:block xl:flex w-full gap-x-16 space-y-12 md:space-y-0 justify-between">
            {/** Products List */}
            <div className="bg-white rounded-lg text-center md:w-[600px]">
              {products.map((product, index) => (
                <div key={index} onClick={() => handleSelectProduct(product)}>
                  <p
                    className={`w-full md:text-xl text-base font-medium py-6 px-6 transition-all duration-300 ${
                      index !== products.length - 1
                        ? "border-b border-[#E0E0E0]"
                        : ""
                    } ${
                      selectedProduct?.title === product.title
                        ? "bg-[#014F9F] text-white"
                        : ""
                    } ${index === 0 ? "rounded-t-lg" : ""} ${
                      index === products.length - 1 ? "rounded-b-lg" : ""
                    } cursor-pointer`}
                  >
                    {product.title}
                  </p>
                </div>
              ))}
            </div>
            {/** Product Details */}
            <div className="w-full relative">
              {selectedProduct && (
                <div className="text-left space-y-8 xl:flex md:space-x-4">
                  <div className="space-y-4 md:w-[442px]">
                    <p className="md:text-3xl leading-[60px] text-base font-light">
                      {selectedProduct.title}
                    </p>
                    <p className="md:text-lg text-base font-normal text-[#5B5D60]">
                      {Array.isArray((selectedProduct as any).highlightPhrases)
                        ? highlightText(
                            selectedProduct.description,
                            (selectedProduct as any).highlightPhrases
                          )
                        : selectedProduct.description}
                    </p>
                    <div className="!mt-8 space-y-6">
                      {selectedProduct?.features?.map(
                        (feature: string, index: number) => (
                          <div className="flex items-center space-x-2">
                            <img
                              src="/assets/svg/check.svg"
                              alt="Check"
                              className="w-6 h-6"
                              loading="lazy"
                              decoding="async"
                            />
                            <p
                              key={index}
                              className="md:text-base text-sm font-medium text-[#222222]"
                            >
                              {feature}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <img
                    src={selectedProduct.img}
                    alt={selectedProduct.title}
                    className="w-[404px] h-[362px] rounded-lg"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              )}
            </div>
          </div>
          {/** Mobile Products List */}
          <div className="md:hidden space-y-4">
            <select
              className="w-full p-4 rounded-lg border border-[#E0E0E0] font-normal text-[#5B5D60] text-sm focus:outline-none focus:ring-1 focus:ring-[#014F9F] focus:border-[#014F9F]"
              value={selectedProductIndex}
              onChange={(e) => {
                setSelectedProductIndex(Number(e.target.value));
              }}
            >
              {products.map((product, index) => (
                <option key={index} value={index}>
                  {product.title}
                </option>
              ))}
            </select>
            {selectedProduct && (
              <div className="space-y-4">
                <img
                  src={selectedProduct.img}
                  alt={selectedProduct.title}
                  className="w-full h-[152px] object-cover rounded-lg"
                />
                <p className="text-2xl text-left font-light">
                  {selectedProduct.title}
                </p>
                <p className="text-sm text-left font-normal text-[#5B5D60]">
                  {Array.isArray((selectedProduct as any).highlightPhrases)
                    ? highlightText(
                        selectedProduct.description,
                        (selectedProduct as any).highlightPhrases
                      )
                    : selectedProduct.description}
                </p>
                <div className="!mt-4 space-y-4">
                  {selectedProduct?.features?.map(
                    (feature: string, index: number) => (
                      <div className="flex items-center space-x-2">
                        <img
                          src="/assets/svg/check.svg"
                          alt="Check"
                          className="w-6 h-6"
                          loading="lazy"
                          decoding="async"
                        />
                        <p
                          key={index}
                          className="md:text-base text-sm text-left font-medium text-[#222222]"
                        >
                          {feature}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="!mt-16 md:mt-0 flex justify-end">
            <div className="space-y-4 md:p-8 p-4 md:rounded-[40px] rounded-2xl border border-[#0019734D] p-4 text-center">
              <p className="text-[32px] font-medium text-[#001973]">
                {t('ProductsPage.content.resources.title')}
              </p>
              <p className="text-lg font-normal text-[#222222]">
                {t('ProductsPage.content.resources.subtitle')}
              </p>
              <button
                onClick={() => setShowResources(true)}
                className="bg-[#0B4494] hover:bg-[#0271BA] transition-all duration-300 text-white px-6 py-2 rounded-[40px]"
              >
                {t('ProductsPage.content.resources.openButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Modal */}
      {showResources && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowResources(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl max-h-[90vh] w-full p-8 md:py-16 py-8 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="top-0 bg-white p-6 flex justify-end items-center">
              <button
                onClick={() => setShowResources(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors text-3xl"
              >
                <img src="/assets/svg/x.svg" alt="Close" className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-8 md:mx-24">
              <p className="text-4xl md:text-5xl text-[#222222] font-semibold">
                {t('ProductsPage.content.resources.modalTitle')}
              </p>
              <h3 className="mt-8 text-2xl md:text-[32px] text-[#022D6C] font-medium">
                {t('ProductsPage.content.resources.gaugesTitle')}
              </h3>
              <div className="md:flex justify-center space-y-8 md:space-y-0">
                <div className="space-y-4 border border-[#001973] rounded-[40px] py-6 md:py-6 px-4 md:px-16">
                  <p className="text-2xl font-medium text-[#222222]">{t('ProductsPage.content.resources.metric')}</p>
                  <button
                    onClick={() => handleDownloadPDF("Calibres-2026-web.pdf")}
                    className="bg-[#0B4494] hover:bg-[#0271BA] transition-all duration-300 text-white px-6 py-2 rounded-[40px]"
                  >
                    {t('ProductsPage.content.resources.downloadButton')}
                  </button>
                </div>
              </div>
              <h3 className="mt-8 text-2xl md:text-[32px] text-[#022D6C] font-medium">
                {t('ProductsPage.content.resources.areasTitle')}
              </h3>
              <div className="md:flex justify-center space-y-8 md:space-y-0">
                <div className="space-y-4 border border-[#001973] rounded-[40px] py-6 md:py-6 px-4 md:px-16">
                  <p className="text-2xl font-medium text-[#222222]">
                    {t('ProductsPage.content.resources.conversions')}
                  </p>
                  <button
                    onClick={() => handleDownloadPDF("Calculo-de Pesos-Rollos-web.pdf")}
                    className="bg-[#0B4494] hover:bg-[#0271BA] transition-all duration-300 text-white px-6 py-2 rounded-[40px]"
                  >
                    {t('ProductsPage.content.resources.downloadButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
